export const FAVORITE_MODE_AUTO = '';
export const FAVORITE_MODE_BRAND = 'brand';
export const FAVORITE_MODE_BROAD = 'broad';

export const FAVORITE_MODE_OPTIONS = [
  { value: FAVORITE_MODE_AUTO, label: 'Best guess' },
  { value: FAVORITE_MODE_BRAND, label: 'Brand / chain' },
  { value: FAVORITE_MODE_BROAD, label: 'Broad' },
];

export const FAVORITE_MODE_COPY = {
  [FAVORITE_MODE_AUTO]: {
    placeholder: 'e.g. Whole Foods Market or jiu jitsu',
    examples: 'Leave unselected and Dwellia will make its best guess once, then save that mode for future searches.',
    tooltip: 'Best guess lets Dwellia classify the favorite one time as either Brand / chain or Broad, then save that choice so it does not keep guessing.',
  },
  [FAVORITE_MODE_BRAND]: {
    placeholder: 'e.g. Whole Foods Market, Trader Joe\'s, Orangetheory',
    examples: 'Brand/chain examples: Whole Foods Market, Trader Joe\'s, Starbucks, Orangetheory.',
    tooltip: 'Brand / chain shows matching locations for a specific brand or chain near your search.',
  },
  [FAVORITE_MODE_BROAD]: {
    placeholder: 'e.g. jiu jitsu, yoga studio, coffee shop, climbing gym',
    examples: 'Broad examples: jiu jitsu, yoga studio, coffee shop, climbing gym.',
    tooltip: 'Broad shows multiple places matching a broader type of place or activity.',
  },
};

export function inferFavoriteMode(label = '') {
  const normalized = String(label || '').trim().toLowerCase();
  if (!normalized) return FAVORITE_MODE_BRAND;

  const broadHints = [
    'gym', 'studio', 'coffee', 'shop', 'jiu jitsu', 'yoga', 'pilates', 'restaurant', 'bar',
    'park', 'trail', 'hospital', 'clinic', 'urgent care', 'grocery store', 'pharmacy', 'church',
  ];
  if (broadHints.some((hint) => normalized.includes(hint))) return FAVORITE_MODE_BROAD;

  const wordCount = normalized.split(/\s+/).filter(Boolean).length;
  if (wordCount <= 2) return FAVORITE_MODE_BROAD;
  return FAVORITE_MODE_BRAND;
}

export function createFavorite({
  label = '',
  mode = FAVORITE_MODE_AUTO,
  placeId = '',
  query = '',
  description = '',
} = {}) {
  const trimmedLabel = String(label || '').trim();
  return {
    id: placeId || `${mode || 'auto'}:${trimmedLabel.toLowerCase()}`,
    label: trimmedLabel,
    mode,
    placeId: String(placeId || '').trim(),
    query: String(query || trimmedLabel).trim(),
    description: String(description || '').trim(),
  };
}

export function normalizeFavorite(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed ? createFavorite({ label: trimmed, mode: FAVORITE_MODE_AUTO }) : null;
  }
  if (typeof raw !== 'object') return null;

  const label = String(raw.label || raw.name || raw.query || '').trim();
  if (!label) return null;

  const mode = [FAVORITE_MODE_AUTO, FAVORITE_MODE_BRAND, FAVORITE_MODE_BROAD].includes(raw.mode)
    ? raw.mode
    : FAVORITE_MODE_AUTO;

  return createFavorite({
    label,
    mode,
    placeId: raw.placeId || raw.place_id || '',
    query: raw.query || label,
    description: raw.description || raw.secondaryText || '',
  });
}

export function normalizeFavorites(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const item of list) {
    const fav = normalizeFavorite(item);
    if (!fav) continue;
    const key = `${fav.mode || 'auto'}:${(fav.placeId || fav.label).toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(fav);
  }
  return out;
}

export function favoriteSummaryLabel(favorite) {
  if (!favorite) return '';
  if (favorite.mode === FAVORITE_MODE_BROAD) return 'Broad';
  if (favorite.mode === FAVORITE_MODE_BRAND) return 'Brand';
  return 'Best guess';
}
